
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * DrawingStorageDashboardResponse struct
 */
class DrawingStorageDashboardResponse {
  /**
   * Initialize struct
   * @param {!UUID=} dashboardId
   * @param {!string=} name
   * @param {!number=} version
   * @param {string=} settings
   * @constructor
   */
  constructor (dashboardId = new UUID(), name = '', version = new UInt64(0, 0), settings = null) {
    this.dashboardId = dashboardId
    this.name = name
    this.version = version
    this.settings = settings
  }

  /**
   * Copy struct (shallow copy)
   * @this {!DrawingStorageDashboardResponse}
   * @param {!DrawingStorageDashboardResponse} other Other struct
   * @returns {!DrawingStorageDashboardResponse} This struct
   */
  copy (other) {
    if (other.dashboardId != null) {
      this.dashboardId = new UUID(other.dashboardId)
    } else {
      this.dashboardId = null
    }
    if (other.name != null) {
      this.name = other.name
    } else {
      this.name = null
    }
    if (other.version != null) {
      this.version = UInt64.fromNumber(other.version)
    } else {
      this.version = null
    }
    if (other.settings != null) {
      this.settings = other.settings
    } else {
      this.settings = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!DrawingStorageDashboardResponse}
   * @returns {!DrawingStorageDashboardResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new DrawingStorageDashboardResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new DrawingStorageDashboardResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!DrawingStorageDashboardResponse}
   * @param {!DrawingStorageDashboardResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof DrawingStorageDashboardResponse)) {
      throw new TypeError('Instance of DrawingStorageDashboardResponse is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!DrawingStorageDashboardResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      dashboardId: ((this.dashboardId != null) ? this.dashboardId.toString() : null),
      name: ((this.name != null) ? this.name : null),
      version: ((this.version != null) ? this.version.toNumber() : null),
      settings: ((this.settings != null) ? this.settings : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return DrawingStorageDashboardResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!DrawingStorageDashboardResponse} other Object value
   * @returns {!DrawingStorageDashboardResponse} Created struct
   */
  static fromObject (other) {
    return new DrawingStorageDashboardResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!DrawingStorageDashboardResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return DrawingStorageDashboardResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!DrawingStorageDashboardResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10006
  }
}

export { DrawingStorageDashboardResponse };

/**
 * Fast Binary Encoding DrawingStorageDashboardResponse field model
 */
class FieldModelDrawingStorageDashboardResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.dashboardId = new fbe.FieldModelUUID(buffer, 4 + 4)
    this.name = new fbe.FieldModelString(buffer, this.dashboardId.fbeOffset + this.dashboardId.fbeSize)
    this.version = new fbe.FieldModelUInt64(buffer, this.name.fbeOffset + this.name.fbeSize)
    this.settings = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.version.fbeOffset + this.version.fbeSize), buffer, this.version.fbeOffset + this.version.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelDrawingStorageDashboardResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelDrawingStorageDashboardResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.dashboardId.fbeSize + this.name.fbeSize + this.version.fbeSize + this.settings.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelDrawingStorageDashboardResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.dashboardId.fbeExtra + this.name.fbeExtra + this.version.fbeExtra + this.settings.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelDrawingStorageDashboardResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelDrawingStorageDashboardResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelDrawingStorageDashboardResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10006
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelDrawingStorageDashboardResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelDrawingStorageDashboardResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.dashboardId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.dashboardId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.dashboardId.fbeSize

    if ((fbeCurrentSize + this.name.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.name.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.name.fbeSize

    if ((fbeCurrentSize + this.version.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.version.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.version.fbeSize

    if ((fbeCurrentSize + this.settings.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.settings.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.settings.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelDrawingStorageDashboardResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelDrawingStorageDashboardResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelDrawingStorageDashboardResponse}
   * @param {!DrawingStorageDashboardResponse} fbeValue Default value, defaults is new DrawingStorageDashboardResponse()
   * @returns {!DrawingStorageDashboardResponse} DrawingStorageDashboardResponse value
   */
  get (fbeValue = new DrawingStorageDashboardResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelDrawingStorageDashboardResponse}
   * @param {!DrawingStorageDashboardResponse} fbeValue DrawingStorageDashboardResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.dashboardId.fbeSize) <= fbeStructSize) {
      fbeValue.dashboardId = this.dashboardId.get()
    } else {
      fbeValue.dashboardId = new UUID()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.dashboardId.fbeSize

    if ((fbeCurrentSize + this.name.fbeSize) <= fbeStructSize) {
      fbeValue.name = this.name.get()
    } else {
      fbeValue.name = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.name.fbeSize

    if ((fbeCurrentSize + this.version.fbeSize) <= fbeStructSize) {
      fbeValue.version = this.version.get(new UInt64(0, 0))
    } else {
      fbeValue.version = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.version.fbeSize

    if ((fbeCurrentSize + this.settings.fbeSize) <= fbeStructSize) {
      fbeValue.settings = this.settings.get()
    } else {
      fbeValue.settings = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.settings.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelDrawingStorageDashboardResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelDrawingStorageDashboardResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelDrawingStorageDashboardResponse}
   * @param {!DrawingStorageDashboardResponse} fbeValue DrawingStorageDashboardResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelDrawingStorageDashboardResponse}
   * @param {DrawingStorageDashboardResponse} fbeValue DrawingStorageDashboardResponse value
   */
  setFields (fbeValue) {
    this.dashboardId.set(fbeValue.dashboardId)
    this.name.set(fbeValue.name)
    this.version.set(fbeValue.version)
    this.settings.set(fbeValue.settings)
  }
}

export { FieldModelDrawingStorageDashboardResponse };

/**
 * Fast Binary Encoding DrawingStorageDashboardResponse model
 */
class DrawingStorageDashboardResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelDrawingStorageDashboardResponse(this.buffer, 4)
  }

  /**
   * Get the DrawingStorageDashboardResponse model
   * @this {!DrawingStorageDashboardResponseModel}
   * @returns {!FieldModelDrawingStorageDashboardResponse} model DrawingStorageDashboardResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!DrawingStorageDashboardResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!DrawingStorageDashboardResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return DrawingStorageDashboardResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!DrawingStorageDashboardResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelDrawingStorageDashboardResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!DrawingStorageDashboardResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!DrawingStorageDashboardResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!DrawingStorageDashboardResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!DrawingStorageDashboardResponseModel}
   * @param {!DrawingStorageDashboardResponse} value DrawingStorageDashboardResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!DrawingStorageDashboardResponseModel}
   * @param {!DrawingStorageDashboardResponse} value DrawingStorageDashboardResponse value, defaults is new DrawingStorageDashboardResponse()
   * @return {!object} Deserialized DrawingStorageDashboardResponse value and its size
   */
  deserialize (value = new DrawingStorageDashboardResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new DrawingStorageDashboardResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new DrawingStorageDashboardResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!DrawingStorageDashboardResponseModel}
   * @param {!number} prev Previous DrawingStorageDashboardResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { DrawingStorageDashboardResponseModel };
